import { ApiV1_Category, ApiV1_Product } from '@pedix-workspace/api-v1';
import { getPropInSelectedLanguage, SUPPORTED_LANGUAGES } from '@pedix-workspace/utils';
import {
  createSearchIndex,
  SearchIndex,
  createScoredSearchIndex,
  ScoredSearchIndex,
  WeightedKeyword,
} from '@pedix-workspace/utils';

export type SearchCatalogIndex = SearchIndex<ApiV1_Product>;

export type SearchResults = {
  category: ApiV1_Category;
  indexedProducts: {
    listIndex: number;
    product: ApiV1_Product;
  }[];
}[];

export type SearchCatalogIndexOptions = {
  selectedLanguage?: SUPPORTED_LANGUAGES;
  displaySku?: boolean;
};

export function createSearchCatalogIndex(
  categories: ApiV1_Category[],
  products: ApiV1_Product[],
  options: SearchCatalogIndexOptions,
): SearchCatalogIndex {
  const categoriesMap: Record<string, ApiV1_Category> = categories.reduce(
    (mapedCategories, category) =>
      Object.assign(mapedCategories, {
        [category.id]: category,
      }),
    {},
  );

  return createSearchIndex(products, {
    indexedKeywordsFn(entry) {
      const keywords = [
        getPropInSelectedLanguage(entry, 'name', options.selectedLanguage, true),
        getPropInSelectedLanguage(entry, 'groupName', options.selectedLanguage),
        getPropInSelectedLanguage(
          categoriesMap[entry.categoryId],
          'name',
          options.selectedLanguage,
          true,
        ),
        ...(entry.tags || []).map(productTag =>
          getPropInSelectedLanguage(productTag, 'tag', options.selectedLanguage, true),
        ),
      ];

      if (entry.presentations) {
        entry.presentations.items.forEach(item => {
          keywords.push(getPropInSelectedLanguage(item, 'name', options.selectedLanguage, true));
        });
      }

      if (options.displaySku && entry.sku) {
        keywords.push(entry.sku);
      }

      return keywords;
    },
  });
}

// Pesos para scoring de búsqueda
const SEARCH_WEIGHTS = {
  NAME: 100,
  TAGS: 50,
  CATEGORY: 10,
  GROUP: 10,
  PRESENTATIONS: 100,
  SKU: 10,
};

export type ScoredSearchCatalogIndex = ScoredSearchIndex<ApiV1_Product>;

export function createScoredSearchCatalogIndex(
  categories: ApiV1_Category[],
  products: ApiV1_Product[],
  options: SearchCatalogIndexOptions,
): ScoredSearchCatalogIndex {
  const categoriesMap: Record<string, ApiV1_Category> = categories.reduce(
    (mapedCategories, category) =>
      Object.assign(mapedCategories, {
        [category.id]: category,
      }),
    {},
  );

  return createScoredSearchIndex(products, {
    weightedKeywordsFn(entry): WeightedKeyword[] {
      const keywords: WeightedKeyword[] = [
        {
          keyword: getPropInSelectedLanguage(entry, 'name', options.selectedLanguage, true),
          weight: SEARCH_WEIGHTS.NAME,
        },
        {
          keyword: getPropInSelectedLanguage(entry, 'groupName', options.selectedLanguage),
          weight: SEARCH_WEIGHTS.GROUP,
        },
        {
          keyword: getPropInSelectedLanguage(
            categoriesMap[entry.categoryId],
            'name',
            options.selectedLanguage,
            true,
          ),
          weight: SEARCH_WEIGHTS.CATEGORY,
        },
        ...(entry.tags || []).map(productTag => ({
          keyword: getPropInSelectedLanguage(productTag, 'tag', options.selectedLanguage, true),
          weight: SEARCH_WEIGHTS.TAGS,
        })),
      ];

      if (entry.presentations) {
        entry.presentations.items.forEach(item => {
          keywords.push({
            keyword: getPropInSelectedLanguage(item, 'name', options.selectedLanguage, true),
            weight: SEARCH_WEIGHTS.PRESENTATIONS,
          });
        });
      }

      if (options.displaySku && entry.sku) {
        keywords.push({
          keyword: entry.sku,
          weight: SEARCH_WEIGHTS.SKU,
        });
      }

      return keywords;
    },
  });
}
